import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Brush,
  Camera,
  ChevronDown,
  Copy,
  Dices,
  Download,
  Film,
  Grid3x3,
  ImageIcon,
  Lock,
  LockOpen,
  Maximize2,
  Minus,
  Newspaper,
  Pencil,
  RotateCw,
  Shapes,
  Sparkles,
  X,
} from 'lucide-react'
import {
  useGenerateImage,
  useImageModels,
  useImageStyles,
  type GenerateImageInput,
  type ImageAspectRatio,
  type ImageProvider,
} from '@/api/endpoints/imageGeneration'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import styles from './ImageGenerationPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

// --- Static configs ----------------------------------------------------------

const PROVIDERS: ReadonlyArray<SegmentedOption<ImageProvider>> = [
  { value: 'hf', label: 'HF · FLUX', title: 'Hugging Face Inference (recomendado)' },
  { value: 'pollinations', label: 'Pollinations', title: 'Sem chave, mais rápido' },
]

const ASPECT_RATIOS: ReadonlyArray<SegmentedOption<ImageAspectRatio>> = [
  { value: 'shorts', label: 'Shorts · 9:16' },
  { value: 'video', label: 'Vídeo · 16:9' },
  { value: 'avatar', label: 'Avatar · 1:1' },
]

// Style key → Lucide icon. Keeps style metadata on the backend (registry)
// while letting the UI pick a glyph. Unknown styles fall back to ImageIcon.
const STYLE_ICONS: Record<string, typeof Pencil> = {
  sketch: Pencil,
  photo: Camera,
  watercolor: Brush,
  anime: Sparkles,
  '3d': Box,
  pixel: Grid3x3,
  vector: Shapes,
  cinematic: Film,
  editorial: Newspaper,
  none: Minus,
}

const PROMPT_EXAMPLES = [
  'a cozy bookstore at golden hour, warm light',
  'an astronaut floating above a glowing nebula',
  'a vintage globe on a wooden desk, soft window light',
  'a wolf howling on a snowy mountain ridge',
] as const

// Rotating loading messages — surfaced while the mutation is pending so the
// user can tell something's happening during the 30-46s HF cold start.
const LOADING_MESSAGES = [
  'Aguardando provider…',
  'Aplicando estilo…',
  'Aquecendo modelo…',
  'Quase lá…',
] as const

// --- Persisted UI state ------------------------------------------------------

interface PersistedState {
  provider: ImageProvider
  style: string
  aspectRatio: ImageAspectRatio
  modelByProvider: { hf: string; pollinations: string }
}

const DEFAULT_STATE: PersistedState = {
  provider: 'hf',
  style: 'sketch',
  aspectRatio: 'shorts',
  modelByProvider: { hf: '', pollinations: '' },
}

// --- Session gallery (memory only — clears on reload, by design) -------------

interface GalleryEntry {
  id: string
  url: string
  prompt: string
  provider: ImageProvider
  style: string
  aspectRatio: ImageAspectRatio
  model: string
  seed: number | null
}

const GALLERY_LIMIT = 6

// --- Component ---------------------------------------------------------------

export function ImageGenerationPage() {
  const [persisted, setPersisted] = useLocalStorage<PersistedState>(
    'prism.tools.image-gen.state',
    DEFAULT_STATE,
  )
  // Migrate old shapes that lack newer keys without nuking the user's prefs.
  const safeState: PersistedState = {
    ...DEFAULT_STATE,
    ...persisted,
    modelByProvider: { ...DEFAULT_STATE.modelByProvider, ...persisted.modelByProvider },
  }
  const { provider, style, aspectRatio, modelByProvider } = safeState

  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [seed, setSeed] = useState<string>('')
  const [seedLocked, setSeedLocked] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [resultMime, setResultMime] = useState<string>('image/png')
  const [resultParams, setResultParams] = useState<GalleryEntry | null>(null)
  const [gallery, setGallery] = useState<GalleryEntry[]>([])
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0)

  // O comentário do lightbox lá embaixo sempre prometeu "Backdrop + Esc
  // closes", mas o Esc nunca existiu — só o clique no fundo fechava. Como o
  // viewer não usa o componente Dialog, não há nada que trate a tecla por ele.
  useEffect(() => {
    if (!lightboxOpen) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setLightboxOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [lightboxOpen])

  const galleryRef = useRef<GalleryEntry[]>([])
  galleryRef.current = gallery
  const stylesQuery = useImageStyles()
  const modelsQuery = useImageModels()
  const generate = useGenerateImage()

  // Free every gallery URL on unmount. While the page is alive the gallery
  // LRU below revokes URLs as they fall off the top-6 window — touching the
  // currently-displayed URL there breaks restore-from-gallery clicks because
  // <img> refetches when src changes (cached pixels survive but a revoked
  // URL fails the new fetch). Gallery is the single source of truth for URL
  // lifecycle, no per-result ref needed.
  useEffect(() => {
    return () => {
      for (const entry of galleryRef.current) URL.revokeObjectURL(entry.url)
    }
  }, [])

  // Rotate the loading message every 4s so the long HF cold start
  // doesn't look frozen.
  useEffect(() => {
    if (!generate.isPending) {
      setLoadingMsgIdx(0)
      return
    }
    const id = window.setInterval(() => {
      setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length)
    }, 4000)
    return () => {
      window.clearInterval(id)
    }
  }, [generate.isPending])

  const updatePersisted = (patch: Partial<PersistedState>) => {
    setPersisted({ ...safeState, ...patch })
  }

  const currentModel = modelByProvider[provider] || modelsQuery.data?.[provider][0]?.key || ''

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = prompt.trim()
    if (!trimmed || generate.isPending) return

    const seedNum = seed.trim() === '' ? null : Number(seed)
    const seedToSend = !Number.isNaN(seedNum) && seedNum !== null ? Math.trunc(seedNum) : null

    const input: GenerateImageInput = {
      prompt: trimmed,
      provider,
      style,
      aspectRatio,
      model: currentModel,
      seed: seedToSend,
    }
    const trimmedNeg = negativePrompt.trim()
    if (trimmedNeg) input.negativePrompt = trimmedNeg

    generate.mutate(
      input,
      {
        onSuccess: ({ url, blob }) => {
          setResultUrl(url)
          setResultMime(blob.type || 'image/png')
          const entry: GalleryEntry = {
            id: `gen-${String(Date.now())}`,
            url,
            prompt: trimmed,
            provider,
            style,
            aspectRatio,
            model: currentModel,
            seed: seedToSend,
          }
          setResultParams(entry)
          setGallery((prev) => {
            const next = [entry, ...prev].slice(0, GALLERY_LIMIT)
            // Free URLs that fell off the window
            for (const old of prev.slice(GALLERY_LIMIT - 1)) {
              if (old.url !== url) URL.revokeObjectURL(old.url)
            }
            return next
          })
        },
      },
    )
  }

  // Cmd/Ctrl + Enter from the textarea submits the form. Stays inside the
  // textarea handler (rather than a global key listener) so it doesn't
  // hijack Enter on other inputs in the same page.
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleReroll = () => {
    if (!seedLocked) {
      // New seed each call by clearing — backend defaults to provider's RNG.
      setSeed('')
    }
    handleSubmit()
  }

  const handleRestoreFromGallery = (entry: GalleryEntry) => {
    setPrompt(entry.prompt)
    updatePersisted({
      provider: entry.provider,
      style: entry.style,
      aspectRatio: entry.aspectRatio,
      modelByProvider: { ...modelByProvider, [entry.provider]: entry.model },
    })
    setSeed(entry.seed !== null ? String(entry.seed) : '')
    setResultUrl(entry.url)
    setResultParams(entry)
  }

  const handleCopyParams = async () => {
    if (!resultParams) return
    const payload = {
      prompt: resultParams.prompt,
      provider: resultParams.provider,
      style: resultParams.style,
      aspect_ratio: resultParams.aspectRatio,
      model: resultParams.model,
      seed: resultParams.seed,
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
    } catch {
      /* clipboard blocked — silently no-op */
    }
  }

  const errMsg = generate.isError ? errorMessage(generate.error, 'Falha ao gerar imagem.') : null
  const isAvatar = aspectRatio === 'avatar'
  const downloadName = isAvatar
    ? resultMime === 'image/jpeg'
      ? 'prism-avatar.jpg'
      : 'prism-avatar.png'
    : resultMime === 'image/jpeg'
      ? 'prism-image.jpg'
      : 'prism-image.png'
  const aspectClass =
    aspectRatio === 'shorts'
      ? styles.aspectShorts
      : aspectRatio === 'video'
        ? styles.aspectVideo
        : styles.aspectAvatar
  const modelOptions = modelsQuery.data?.[provider] ?? []

  const styleList = useMemo(() => stylesQuery.data ?? [], [stylesQuery.data])

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Tools"
        title="Geração de imagem"
        description="Gere imagens a partir de um prompt. HF (FLUX) é recomendado pra qualidade; Pollinations pra rascunho rápido."
      />

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* PROMPT */}
        <Section
          title="Prompt"
          description="Descreva a imagem em inglês para melhor consistência. ⌘/Ctrl + Enter envia."
        >
          <textarea
            className={styles.textarea}
            placeholder="ex: a cozy bookstore at golden hour, warm light, ultra detailed"
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value)
            }}
            onKeyDown={handleTextareaKeyDown}
            rows={4}
            disabled={generate.isPending}
          />
          <div className={styles.examples}>
            {PROMPT_EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                className={styles.exampleChip}
                onClick={() => {
                  setPrompt(ex)
                }}
                disabled={generate.isPending}
                title="Usar como prompt"
              >
                {ex}
              </button>
            ))}
          </div>
        </Section>

        {/* STYLE */}
        <Section title="Estilo" description="Define o sufixo aplicado ao prompt antes da geração.">
          {stylesQuery.isPending ? (
            <p className={styles.muted}>Carregando estilos…</p>
          ) : stylesQuery.isError ? (
            <QueryErrorNotice
              error={stylesQuery.error}
              label="a lista de estilos"
              onRetry={() => {
                void stylesQuery.refetch()
              }}
            />
          ) : (
            <div className={styles.styleGrid}>
              {styleList.map((s) => {
                const Icon = STYLE_ICONS[s.key] ?? ImageIcon
                const isActive = style === s.key
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={cn(styles.styleCard, isActive && styles.styleCardActive)}
                    onClick={() => {
                      updatePersisted({ style: s.key })
                    }}
                    disabled={generate.isPending}
                    aria-pressed={isActive}
                  >
                    <Icon className={styles.styleIcon} aria-hidden />
                    <span className={styles.styleLabel}>{s.label}</span>
                    <span className={styles.styleDesc}>{s.description}</span>
                  </button>
                )
              })}
            </div>
          )}
        </Section>

        {/* CONFIG */}
        <Section title="Configurações">
          <div className={styles.configGrid}>
            <div className={styles.configRow}>
              {/* <span>, não <label>: o Segmented não expõe controle nativo
                  para htmlFor e já carrega aria-label. Mesmo caso do
                  ChannelsPage. */}
              <span className={styles.configLabel}>Provider</span>
              <Segmented<ImageProvider>
                options={PROVIDERS}
                value={provider}
                onValueChange={(v) => {
                  updatePersisted({ provider: v })
                }}
                aria-label="Provider"
              />
            </div>

            <div className={styles.configRow}>
              <span className={styles.configLabel}>Formato</span>
              <Segmented<ImageAspectRatio>
                options={ASPECT_RATIOS}
                value={aspectRatio}
                onValueChange={(v) => {
                  // Switching to avatar nudges the style to "photo" — sketches
                  // and pixel art rarely make good profile pictures, and photo
                  // is the most common choice for a YouTube channel avatar.
                  // Switching away keeps whatever style the user has, even if
                  // it's still photo from the previous switch.
                  if (v === 'avatar') {
                    updatePersisted({ aspectRatio: v, style: 'photo' })
                  } else {
                    updatePersisted({ aspectRatio: v })
                  }
                }}
                aria-label="Aspect ratio"
              />
            </div>

            <div className={styles.configRow}>
              <label className={styles.configLabel} htmlFor="image-model-select">
                Modelo
              </label>
              {modelsQuery.isPending ? (
                <span className={styles.muted}>Carregando…</span>
              ) : (
                <SelectRoot
                  value={currentModel}
                  onValueChange={(v) => {
                    updatePersisted({
                      modelByProvider: { ...modelByProvider, [provider]: v },
                    })
                  }}
                  disabled={generate.isPending}
                >
                  <SelectTrigger id="image-model-select">
                    <SelectValue placeholder="Default" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {m.label} · ~{String(m.avg_latency_s)}s
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            </div>
          </div>

          {/* ADVANCED accordion */}
          <button
            type="button"
            className={styles.advancedToggle}
            onClick={() => {
              setAdvancedOpen((o) => !o)
            }}
            aria-expanded={advancedOpen}
          >
            <ChevronDown
              className={cn(styles.advancedChevron, advancedOpen && styles.advancedChevronOpen)}
              aria-hidden
            />
            <span>Avançado</span>
          </button>
          {advancedOpen ? (
            <div className={styles.advancedBody}>
              <label className={styles.advField}>
                <span className={styles.advLabel}>Negative prompt</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="ex: blurry, deformed, low quality"
                  value={negativePrompt}
                  onChange={(e) => {
                    setNegativePrompt(e.target.value)
                  }}
                  disabled={generate.isPending}
                />
                <span className={styles.advHint}>
                  Vai ser combinado com o negative do estilo selecionado.
                </span>
              </label>

              <label className={styles.advField}>
                <span className={styles.advLabel}>Seed</span>
                <div className={styles.seedRow}>
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="aleatório"
                    value={seed}
                    onChange={(e) => {
                      setSeed(e.target.value)
                    }}
                    disabled={generate.isPending}
                  />
                  <IconButton
                    aria-label={seedLocked ? 'Destravar seed' : 'Travar seed'}
                    aria-pressed={seedLocked}
                    onClick={() => {
                      setSeedLocked((l) => !l)
                    }}
                    title={seedLocked ? 'Seed mantida no re-roll' : 'Seed nova a cada re-roll'}
                  >
                    {seedLocked ? <Lock /> : <LockOpen />}
                  </IconButton>
                </div>
                <span className={styles.advHint}>
                  Mesmo seed + mesmo prompt = mesma imagem (reproduzível).
                </span>
              </label>
            </div>
          ) : null}
        </Section>

        <div className={styles.submitRow}>
          <Button
            type="submit"
            variant="primary"
            leadingIcon={<Sparkles />}
            disabled={!prompt.trim() || generate.isPending}
          >
            {generate.isPending ? 'Gerando…' : 'Gerar imagem'}
          </Button>
        </div>
      </form>

      {/* RESULT */}
      <Section title="Resultado">
        <div role="status" aria-live="polite" className={styles.srOnly}>
          {generate.isPending
            ? LOADING_MESSAGES[loadingMsgIdx]
            : errMsg
              ? `Erro: ${errMsg}`
              : resultUrl
                ? 'Imagem gerada.'
                : ''}
        </div>

        {generate.isPending ? (
          <div className={cn(styles.resultCard, aspectClass, styles.skeleton)}>
            <span className={styles.skeletonShimmer} />
            <span className={styles.skeletonMessage}>{LOADING_MESSAGES[loadingMsgIdx]}</span>
          </div>
        ) : null}

        {!generate.isPending && errMsg ? (
          <div className={styles.errorCard}>
            <X className={styles.errorIcon} aria-hidden />
            <div className={styles.errorBody}>
              <strong>Falha ao gerar</strong>
              <p>{errMsg}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<RotateCw />}
              onClick={() => {
                handleSubmit()
              }}
            >
              Tentar de novo
            </Button>
          </div>
        ) : null}

        {!generate.isPending && !errMsg && !resultUrl ? (
          <div className={cn(styles.empty, aspectClass)}>
            <ImageIcon aria-hidden />
            <p>Nada gerado ainda. Escreva um prompt e clique em Gerar.</p>
          </div>
        ) : null}

        {!generate.isPending && resultUrl ? (
          <div className={styles.result}>
            <button
              type="button"
              className={cn(styles.resultImageBtn, aspectClass)}
              onClick={() => {
                setLightboxOpen(true)
              }}
              aria-label="Abrir em tela cheia"
            >
              <img src={resultUrl} alt={resultParams?.prompt ?? prompt} className={styles.image} />
              <span className={styles.fullscreenHint}>
                <Maximize2 aria-hidden />
              </span>
            </button>

            <div className={styles.resultActions}>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Dices />}
                onClick={handleReroll}
                disabled={!prompt.trim()}
              >
                Re-roll
              </Button>
              <a
                href={resultUrl}
                download={downloadName}
                className={styles.downloadBtn}
                aria-label="Baixar imagem"
              >
                <Download aria-hidden />
                <span>Baixar</span>
              </a>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Copy />}
                onClick={() => {
                  void handleCopyParams()
                }}
              >
                Copiar params
              </Button>
            </div>

            {isAvatar ? (
              <div className={styles.avatarPreview}>
                <img src={resultUrl} alt="" className={styles.avatarPreviewCircle} />
                <div className={styles.avatarPreviewText}>
                  <strong>Preview como avatar</strong>
                  <span>
                    1024×1024 PNG — corte circular que o YouTube aplica automaticamente.
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Section>

      {/* GALLERY */}
      {gallery.length > 0 ? (
        <Section
          title="Sessão atual"
          description="Últimas gerações desta aba — recarregar a página limpa a galeria."
        >
          <div className={styles.gallery}>
            {gallery.map((g) => (
              <button
                key={g.id}
                type="button"
                className={cn(
                  styles.galleryItem,
                  resultParams?.id === g.id && styles.galleryItemActive,
                )}
                onClick={() => {
                  handleRestoreFromGallery(g)
                }}
                title={g.prompt}
              >
                <img src={g.url} alt={g.prompt} className={styles.galleryThumb} />
                <span className={styles.galleryMeta}>
                  {g.style} · {g.aspectRatio === 'shorts' ? '9:16' : '16:9'}
                </span>
              </button>
            ))}
          </div>
        </Section>
      ) : null}

      {/* LIGHTBOX — minimal manual implementation; Dialog component has too much
          chrome for a borderless image viewer. Backdrop + Esc closes.
          O fechar-no-fundo é um <button> próprio atrás da imagem, e não um
          onClick no container com role="dialog": handler de clique em elemento
          de role não-interativo é inacessível por teclado, e era o que os
          quatro erros de jsx-a11y apontavam. Como botão, ele ganha foco e
          Enter/Espaço de graça. */}
      {lightboxOpen && resultUrl ? (
        <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label="Imagem em tela cheia">
          <button
            type="button"
            className={styles.lightboxBackdrop}
            aria-label="Fechar"
            onClick={() => {
              setLightboxOpen(false)
            }}
          />
          <button
            type="button"
            className={styles.lightboxClose}
            aria-label="Fechar"
            onClick={() => {
              setLightboxOpen(false)
            }}
          >
            <X />
          </button>
          <img src={resultUrl} alt={resultParams?.prompt ?? prompt} className={styles.lightboxImage} />
        </div>
      ) : null}
    </div>
  )
}
